import { useState, ReactNode } from "react";
import ErrorIcon from '@mui/icons-material/Error';
import { useThemeApp } from "components/Theme/theme";
import { ThemedIcon, ThemedIconProps } from "../icons";
import { Box, Typography, Tooltip } from "@mui/material";

export interface ThemedInputContainerProps {
    justifyContentCenter?: boolean;
    icon?: ThemedIconProps;
    heightContent?: string;
    labelCenter?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    children: ReactNode;
    helperText?: string;
    multiline?: boolean;
    required?: boolean;
    disabled?: boolean;
    inputId?: string;
    label?: string;
    error?: boolean;
}

export const ThemedInputContainer = ({
    icon,
    label,
    onBlur,
    inputId,
    onFocus,
    children,
    helperText,
    error = false,
    heightContent,
    disabled = false,
    required = false,
    multiline = false,
    labelCenter = false,
    justifyContentCenter = true,
    ...rest
}: ThemedInputContainerProps) => {
    const { colors } = useThemeApp();
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = () => {
        if (!disabled) {
            setIsFocused(true);
            onFocus?.();
        }
    };

    const handleBlur = () => {
        setIsFocused(false);
        onBlur?.();
    };

    const getBorderColor = () => {
        if (error) return colors?.danger + " !important";
        if (disabled) return colors?.text + "30 !important";
        if (isFocused) return colors?.primary + " !important";
        return colors?.text + "30 !important";
    };

    const getBackgroundColor = () => {
        return colors?.card;
    };

    return (
        <Box sx={{ width: "100%" }}>
            {label && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, mb: 0.5 }}>
                    <Typography
                        htmlFor={inputId}
                        component="label"
                        sx={{
                            fontWeight: 600,
                            display: "block",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            transition: "color 0.2s ease",
                            fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            textAlign: labelCenter ? "center" : "left",
                            color: disabled ? "text.disabled" : (error ? colors?.danger : (isFocused ? colors?.primary : "text.primary")),
                        }}
                    >
                        {label}
                        {required && (
                            <Typography
                                component="span"
                                sx={{
                                    color: error ? colors?.danger : (isFocused ? colors?.primary : "error.main"),
                                    ml: 0.5
                                }}
                            >
                                *
                            </Typography>
                        )}
                    </Typography>

                    {error && helperText && (
                        <Tooltip title={helperText} arrow placement="top">
                            <ErrorIcon
                                sx={{
                                    fontSize: 16,
                                    color: colors?.danger,
                                    cursor: 'help'
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
            )}

            <Box
                sx={{
                    width: "100%",
                    borderWidth: 1,
                    display: "flex",
                    borderRadius: 2,
                    p: "0 !Important",
                    borderStyle: "solid",
                    flexDirection: "column",
                    opacity: disabled ? 0.7 : 1,
                    borderColor: getBorderColor(),
                    backgroundColor: getBackgroundColor(),
                    cursor: disabled ? 'not-allowed' : 'default',
                    height: heightContent ? heightContent : undefined,
                    justifyContent: justifyContentCenter ? "center" : "flex-start",
                    transition: "border-color 0.2s ease, background-color 0.2s ease",
                    minHeight: heightContent ? undefined : multiline ? "5rem" : "4rem",
                    "&:hover": !disabled ? {
                        borderColor: error ? colors?.danger + " !important" : colors?.primary + " !important",
                    } : {},
                }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                {...rest}
            >
                <Box
                    sx={{
                        gap: 1,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        p: { xs: 1, sm: 2 },
                        alignItems: "center",
                        position: "relative",
                    }}
                >
                    {icon && (
                        <ThemedIcon
                            size={24}
                            type={icon.type}
                            name={icon.name}
                            color={disabled ? "danger" : "primary"}
                        />
                    )}

                    <Box
                        sx={{
                            p: 0,
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Box>

        </Box>
    );
};